/**
 * /pricing — Public pricing page for Elreign.
 *
 * Design source: Session C (Claude Design output)
 * Path: paul-handoff/design-iterations/claude-design-output-pricing/elreign/project/Pricing.html
 *
 * Brand vocabulary used here:
 *   --primary-700 (#143832) — primary action / header / closer background
 *   --accent-400 (#C19A5B) — accent (featured tier, CTA)
 *   --accent-600 (#84632F) — brass text on cream (AAA contrast)
 *   --surface (#F4EFE5) — card backgrounds
 *   --bg (#FAF7F0) — page background
 *
 * All styles are scoped under the `.pricing-page` root div to prevent
 * collision with admin-surface .btn-primary/.btn-secondary/.btn-brass
 * defined in design-system.css.
 *
 * Nav links to non-existent routes (/product, /security, /customers) use
 * plain <a> elements to avoid TanStack Router type errors.
 * Links to existing routes use TanStack <Link to="...">.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { ReignMark } from '../components/reign-mark';

export const Route = createFileRoute('/pricing')({
  component: PricingPage,
});

/* ── Inline SVG helpers ──────────────────────────────────────────────────── */

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}

function TickIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

/* ── Page component ──────────────────────────────────────────────────────── */

function PricingPage() {
  return (
    <div className="pricing-page">
      {/* ================================================================ HEADER */}
      <header className="site">
        <div className="shell header-row">
          <Link className="brand-row" to="/" aria-label="Elreign home">
            <ReignMark size={30} />
            <span className="wordmark">elreign</span>
          </Link>
          <nav className="primary">
            <a href="/product">Product</a>
            <a href="/pricing" className="active">Pricing</a>
            <a href="/security">Security</a>
            <a href="/customers">Customers</a>
            <Link to="/about">About</Link>
          </nav>
          <div className="header-right">
            <Link className="signin-link" to="/auth/login">Sign in</Link>
            <Link className="btn btn-primary" to="/contact">Talk to us</Link>
          </div>
        </div>
      </header>

      {/* ================================================================ HERO */}
      <section className="hero">
        <div className="shell">
          <div className="hero-eyebrow">
            <span className="seal" />
            <span className="eyebrow">Pricing</span>
          </div>
          <h1 className="hero-title">Trust-firm pricing, <em>kept honest.</em></h1>
          <p className="hero-sub">
            Firms pay one rate per client, per month, and it drops as the book grows.
            Individuals pay a flat monthly rate. Every tier includes the full audit chain, FICA
            lodgement, and unlimited resolutions, not the features we&rsquo;d rather not
            unbundle.
          </p>
          <div className="hero-note">
            <span className="pip" />
            <span>
              Three trusts included per client &middot; extra trusts billed at your tier rate
              &middot; no fees per signature.
            </span>
          </div>
        </div>
      </section>

      {/* ================================================================ TIERS */}
      <section className="tiers">
        <div className="shell">
          <div className="tier-grid">

            {/* Tier 1 — Independent */}
            <article className="tier" aria-labelledby="tier-1-name">
              <header className="tier-head">
                <div className="eyebrow ink">Tier 01</div>
                <h2 className="tier-name" id="tier-1-name">Independent</h2>
                <p className="tier-for">For an individual administering their own family&rsquo;s trusts.</p>
              </header>

              <div className="tier-price">
                <div className="price-amount num"><span className="currency">R</span>850</div>
                <div className="price-cadence">per month &middot; <b>3 trusts included</b></div>
                <div className="price-range num">
                  Additional trusts <b>R 125 each</b> per month.
                </div>
              </div>

              <div className="tier-features-head">What&rsquo;s included</div>
              <ul className="tier-features">
                <li>
                  <TickIcon className="tick" />
                  <span><b>3 trusts</b> under administration in the base price</span>
                </li>
                <li>
                  <TickIcon className="tick" />
                  <span>Full audit chain: immutable, FICA s23 compliant</span>
                </li>
                <li>
                  <TickIcon className="tick" />
                  <span>Master ICMS lodgement integration</span>
                </li>
                <li>
                  <TickIcon className="tick" />
                  <span>Unlimited resolutions and signatures</span>
                </li>
                <li>
                  <TickIcon className="tick" />
                  <span>Email support, <b>24h response</b></span>
                </li>
              </ul>

              <div className="tier-cta">
                <Link className="btn btn-secondary btn-lg btn-block" to="/auth/signup">Get started</Link>
                <p className="tier-foot-note">Billed monthly, in ZAR</p>
              </div>
            </article>

            {/* Tier 2 — Practice (recommended) */}
            <article className="tier featured" aria-labelledby="tier-2-name">
              <span className="tier-badge">Recommended</span>
              <header className="tier-head">
                <div className="eyebrow">Tier 02</div>
                <h2 className="tier-name" id="tier-2-name"><em>Practice</em></h2>
                <p className="tier-for">For fiduciary firms with <b>fewer than 100 clients</b>.</p>
              </header>

              <div className="tier-price">
                <div className="price-amount num"><span className="currency">R</span>450</div>
                <div className="price-cadence">per client, <b>per month</b></div>
                <div className="price-range num">
                  3 trusts per client included. Additional trusts <b>R 65 each</b> per month.
                </div>
              </div>

              <div className="tier-features-head brass">Everything in Independent, plus</div>
              <ul className="tier-features">
                <li>
                  <TickIcon className="tick" />
                  <span><b>3 trusts per client</b> under administration, included</span>
                </li>
                <li>
                  <TickIcon className="tick" />
                  <span>White-label tenant subdomain: <b>firm.elreign.io</b></span>
                </li>
                <li>
                  <TickIcon className="tick" />
                  {/* 2026-08-25: "voice tone" dropped — the greeting-voice
                      feature was removed from the Branding Studio on Paul's
                      2026-08-23 walkthrough ruling; the pricing page must not
                      advertise what the product no longer has. */}
                  <span>Branding Studio: logo, colours, fonts</span>
                </li>
                <li>
                  <TickIcon className="tick" />
                  <span>Multi-firm audit explorer</span>
                </li>
                <li>
                  <TickIcon className="tick" />
                  <span>Phone support, <b>4h response</b></span>
                </li>
                <li>
                  <TickIcon className="tick" />
                  <span>POPIA Data Processor Agreement included</span>
                </li>
              </ul>

              <div className="tier-cta">
                <Link className="btn btn-brass btn-lg btn-block" to="/auth/signup">Start with Practice</Link>
                <p className="tier-foot-note">Billed monthly, in ZAR</p>
              </div>
            </article>

            {/* Tier 3 — Practice+ */}
            <article className="tier" aria-labelledby="tier-3-name">
              <header className="tier-head">
                <div className="eyebrow ink">Tier 03</div>
                <h2 className="tier-name" id="tier-3-name">Practice<span aria-hidden="true">+</span><span className="visually-hidden"> plus</span></h2>
                <p className="tier-for">For firms with <b>100-500 clients</b>.</p>
              </header>

              <div className="tier-price">
                <div className="price-amount num"><span className="currency">R</span>395</div>
                <div className="price-cadence">per client, <b>per month</b></div>
                <div className="price-range num">
                  3 trusts per client included. Additional trusts <b>R 55 each</b> per month.
                </div>
              </div>

              <div className="tier-features-head">Everything in Practice, plus</div>
              <ul className="tier-features">
                <li>
                  <TickIcon className="tick" />
                  <span><b>Volume pricing</b>: per-client rate drops at scale</span>
                </li>
                <li>
                  <TickIcon className="tick" />
                  <span>Priority phone support, <b>2h response</b></span>
                </li>
                <li>
                  <TickIcon className="tick" />
                  <span>Bulk client onboarding tools</span>
                </li>
                <li>
                  <TickIcon className="tick" />
                  <span>Quarterly portfolio reconciliation reports</span>
                </li>
                <li>
                  <TickIcon className="tick" />
                  <span>Dedicated onboarding manager for the first 60 days</span>
                </li>
              </ul>

              <div className="tier-cta">
                <Link className="btn btn-secondary btn-lg btn-block" to="/auth/signup">Start with Practice+</Link>
                <p className="tier-foot-note">Billed monthly, in ZAR</p>
              </div>
            </article>

            {/* Tier 4 — Enterprise */}
            <article className="tier" aria-labelledby="tier-4-name">
              <header className="tier-head">
                <div className="eyebrow ink">Tier 04</div>
                <h2 className="tier-name" id="tier-4-name">Enterprise</h2>
                <p className="tier-for">For firms with <b>500+ clients</b> or law firms with fiduciary divisions.</p>
              </header>

              <div className="tier-price">
                <div className="price-amount num"><span className="currency">R</span>295</div>
                <div className="price-cadence">per client, <b>per month</b></div>
                <div className="price-range num">
                  3 trusts per client included. Additional trusts <b>R 45 each</b> per month.
                </div>
              </div>

              <div className="tier-features-head">Everything in Practice+, plus</div>
              <ul className="tier-features">
                <li>
                  <TickIcon className="tick" />
                  <span><b>Dedicated tenant</b> on a private Cosmos partition</span>
                </li>
                <li>
                  <TickIcon className="tick" />
                  <span>Custom Cosmos throughput: sized to your read/write profile</span>
                </li>
                <li>
                  <TickIcon className="tick" />
                  <span>SAML SSO and custom OIDC providers: on request</span>
                </li>
                <li>
                  <TickIcon className="tick" />
                  <span>Dedicated CSM with a <b>1h SLA</b></span>
                </li>
                <li>
                  <TickIcon className="tick" />
                  <span>Quarterly compliance review with our team</span>
                </li>
              </ul>

              <div className="tier-cta">
                <Link className="btn btn-secondary btn-lg btn-block" to="/contact">Talk to us</Link>
                <p className="tier-foot-note">
                  Average reply <span className="num">&lt; 1 business day</span>
                </p>
              </div>
            </article>

          </div>
        </div>
      </section>

      {/* ================================================================ COMPARE */}
      <section className="compare">
        <div className="shell">
          <div className="section-head">
            <span className="eyebrow">Feature comparison</span>
            <h2>Every line, <em>on the record.</em></h2>
            <p>
              The full matrix. We unbundle nothing that matters: the audit chain, FICA
              lodgement, and resolutions are in every tier.
            </p>
          </div>

          <div className="compare-tbl-wrap">
            <table className="compare-tbl">
              <thead>
                <tr>
                  <th className="col-feat">Feature</th>
                  <th className="col-tier">
                    <span className="tier-tag">Tier 01</span>
                    Independent
                  </th>
                  <th className="col-tier featured">
                    <span className="tier-tag">Tier 02 &middot; Recommended</span>
                    Practice
                  </th>
                  <th className="col-tier">
                    <span className="tier-tag">Tier 03</span>
                    Enterprise
                  </th>
                </tr>
              </thead>
              <tbody>

                {/* Trust administration */}
                <tr className="section-row">
                  <td colSpan={4}><span className="num">01</span>Trust administration</td>
                </tr>
                <tr>
                  <td className="feat">
                    Trusts in the base price
                    <span className="feat-sub">Additional trusts billed at the per-tier overage rate</span>
                  </td>
                  <td className="col-tier"><span className="cell-text"><b>3</b></span></td>
                  <td className="col-tier featured"><span className="cell-text"><b>3</b> per client</span></td>
                  <td className="col-tier"><span className="cell-text"><b>3</b> per client</span></td>
                </tr>
                <tr>
                  <td className="feat">
                    Per-trust overage
                    <span className="feat-sub">Billed monthly for every trust beyond the included three</span>
                  </td>
                  <td className="col-tier"><span className="cell-text num">R 125</span></td>
                  <td className="col-tier featured"><span className="cell-text num">R 65 &middot; R 55 at scale</span></td>
                  <td className="col-tier"><span className="cell-text num">R 45</span></td>
                </tr>
                <tr>
                  <td className="feat">
                    Designed for
                    <span className="feat-sub">Who the tier is shaped around</span>
                  </td>
                  <td className="col-tier"><span className="cell-text">Individual administering family trusts</span></td>
                  <td className="col-tier featured"><span className="cell-text">Firms up to 500 clients</span></td>
                  <td className="col-tier"><span className="cell-text">Firms with 500+ clients</span></td>
                </tr>
                <tr>
                  <td className="feat">Unlimited resolutions</td>
                  <td className="col-tier"><CheckIcon className="check" /></td>
                  <td className="col-tier featured"><CheckIcon className="check brass" /></td>
                  <td className="col-tier"><CheckIcon className="check" /></td>
                </tr>
                <tr>
                  <td className="feat">
                    Multi-stakeholder signing
                    <span className="feat-sub">Co-trustees, independent trustees, witnesses</span>
                  </td>
                  <td className="col-tier"><CheckIcon className="check" /></td>
                  <td className="col-tier featured"><CheckIcon className="check brass" /></td>
                  <td className="col-tier"><CheckIcon className="check" /></td>
                </tr>
                <tr>
                  <td className="feat">
                    Immutable audit chain
                    <span className="feat-sub">FICA s23 compliant, 5-year WORM retention</span>
                  </td>
                  <td className="col-tier"><CheckIcon className="check" /></td>
                  <td className="col-tier featured"><CheckIcon className="check brass" /></td>
                  <td className="col-tier"><CheckIcon className="check" /></td>
                </tr>

                {/* Compliance */}
                <tr className="section-row">
                  <td colSpan={4}><span className="num">02</span>Compliance</td>
                </tr>
                <tr>
                  <td className="feat">FICA s23 lodgement</td>
                  <td className="col-tier"><CheckIcon className="check" /></td>
                  <td className="col-tier featured"><CheckIcon className="check brass" /></td>
                  <td className="col-tier"><CheckIcon className="check" /></td>
                </tr>
                <tr>
                  <td className="feat">Master ICMS integration</td>
                  <td className="col-tier"><CheckIcon className="check" /></td>
                  <td className="col-tier featured"><CheckIcon className="check brass" /></td>
                  <td className="col-tier"><CheckIcon className="check" /></td>
                </tr>
                <tr>
                  <td className="feat">POPIA s24 erasure workflow</td>
                  <td className="col-tier"><CheckIcon className="check" /></td>
                  <td className="col-tier featured"><CheckIcon className="check brass" /></td>
                  <td className="col-tier"><CheckIcon className="check" /></td>
                </tr>
                <tr>
                  <td className="feat">Beneficial-ownership register (Trust Property Control Act)</td>
                  <td className="col-tier"><CheckIcon className="check" /></td>
                  <td className="col-tier featured"><CheckIcon className="check brass" /></td>
                  <td className="col-tier"><CheckIcon className="check" /></td>
                </tr>

                {/* Branding */}
                <tr className="section-row">
                  <td colSpan={4}><span className="num">03</span>Branding</td>
                </tr>
                <tr>
                  <td className="feat">
                    Tenant subdomain
                    <span className="feat-sub">firm.elreign.io routed through your firm name</span>
                  </td>
                  <td className="col-tier"><span className="dash" aria-label="not included" /></td>
                  <td className="col-tier featured"><CheckIcon className="check brass" /></td>
                  <td className="col-tier"><CheckIcon className="check" /></td>
                </tr>
                <tr>
                  <td className="feat">Branding Studio</td>
                  <td className="col-tier"><span className="dash" aria-label="not included" /></td>
                  <td className="col-tier featured"><CheckIcon className="check brass" /></td>
                  <td className="col-tier"><CheckIcon className="check" /></td>
                </tr>
                <tr>
                  <td className="feat">Custom logo upload</td>
                  <td className="col-tier"><span className="dash" aria-label="not included" /></td>
                  <td className="col-tier featured"><CheckIcon className="check brass" /></td>
                  <td className="col-tier"><CheckIcon className="check" /></td>
                </tr>
                <tr>
                  <td className="feat">
                    Custom brand colours
                    <span className="feat-sub">Primary, accent, neutrals: AA contrast checked</span>
                  </td>
                  <td className="col-tier"><span className="dash" aria-label="not included" /></td>
                  <td className="col-tier featured"><CheckIcon className="check brass" /></td>
                  <td className="col-tier"><CheckIcon className="check" /></td>
                </tr>

                {/* Authentication */}
                <tr className="section-row">
                  <td colSpan={4}><span className="num">04</span>Authentication</td>
                </tr>
                <tr>
                  <td className="feat">Multi-factor auth (TOTP, SMS)</td>
                  <td className="col-tier"><CheckIcon className="check" /></td>
                  <td className="col-tier featured"><CheckIcon className="check brass" /></td>
                  <td className="col-tier"><CheckIcon className="check" /></td>
                </tr>
                <tr>
                  <td className="feat">Step-up auth on resolutions</td>
                  <td className="col-tier"><CheckIcon className="check" /></td>
                  <td className="col-tier featured"><CheckIcon className="check brass" /></td>
                  <td className="col-tier"><CheckIcon className="check" /></td>
                </tr>
                <tr>
                  <td className="feat">WebAuthn / passkey</td>
                  <td className="col-tier"><CheckIcon className="check" /></td>
                  <td className="col-tier featured"><CheckIcon className="check brass" /></td>
                  <td className="col-tier"><CheckIcon className="check" /></td>
                </tr>
                <tr>
                  {/* 2026-08-05: "on request" is deliberate and load-bearing. SAML/OIDC
                      is NOT implemented — the only saml/oidc references in the codebase
                      are these pricing labels. Paul's ruling: we can do it on request.
                      Do not restore this to an unqualified tick until it ships. */}
                  <td className="feat">SAML SSO (on request)</td>
                  <td className="col-tier"><span className="dash" aria-label="not included" /></td>
                  <td className="col-tier featured"><span className="dash" aria-label="not included" /></td>
                  <td className="col-tier"><CheckIcon className="check" /></td>
                </tr>
                <tr>
                  <td className="feat">Custom OIDC providers <span className="feat-note">(on request)</span></td>
                  <td className="col-tier"><span className="dash" aria-label="not included" /></td>
                  <td className="col-tier featured"><span className="dash" aria-label="not included" /></td>
                  <td className="col-tier"><CheckIcon className="check" /></td>
                </tr>

                {/* Support */}
                <tr className="section-row">
                  <td colSpan={4}><span className="num">05</span>Support</td>
                </tr>
                <tr>
                  <td className="feat">Email support</td>
                  <td className="col-tier"><span className="cell-text num"><b>24h</b> response</span></td>
                  <td className="col-tier featured"><span className="cell-text num"><b>8h</b> response</span></td>
                  <td className="col-tier"><span className="cell-text num"><b>2h</b> response</span></td>
                </tr>
                <tr>
                  <td className="feat">Phone support</td>
                  <td className="col-tier"><span className="dash" aria-label="not included" /></td>
                  <td className="col-tier featured"><span className="cell-text num"><b>4h</b> response</span></td>
                  <td className="col-tier"><span className="cell-text num"><b>1h</b> response</span></td>
                </tr>
                <tr>
                  <td className="feat">Dedicated CSM</td>
                  <td className="col-tier"><span className="dash" aria-label="not included" /></td>
                  <td className="col-tier featured"><span className="dash" aria-label="not included" /></td>
                  <td className="col-tier"><CheckIcon className="check" /></td>
                </tr>
                <tr>
                  <td className="feat">Contractual SLA</td>
                  <td className="col-tier"><span className="cell-text">Standard</span></td>
                  <td className="col-tier featured"><span className="cell-text"><b>99.9%</b></span></td>
                  <td className="col-tier"><span className="cell-text"><b>99.95%</b></span></td>
                </tr>

                {/* Infrastructure */}
                <tr className="section-row">
                  <td colSpan={4}><span className="num">06</span>Infrastructure</td>
                </tr>
                <tr>
                  <td className="feat">
                    Cosmos partition
                    <span className="feat-sub">Azure Cosmos DB tenancy isolation</span>
                  </td>
                  <td className="col-tier"><span className="cell-text">Shared</span></td>
                  <td className="col-tier featured"><span className="cell-text"><b>Shared</b></span></td>
                  <td className="col-tier"><span className="cell-text"><b>Dedicated</b></span></td>
                </tr>
                <tr>
                  <td className="feat">
                    Custom RU/s throughput
                    <span className="feat-sub">Provisioned read/write units, sized to your book</span>
                  </td>
                  <td className="col-tier"><span className="dash" aria-label="not included" /></td>
                  <td className="col-tier featured"><span className="dash" aria-label="not included" /></td>
                  <td className="col-tier"><CheckIcon className="check" /></td>
                </tr>
                <tr>
                  <td className="feat">
                    Region
                    <span className="feat-sub">All data resident in South Africa</span>
                  </td>
                  <td className="col-tier">
                    <span className="cell-text"><span className="sub">southafricanorth</span></span>
                  </td>
                  <td className="col-tier featured">
                    <span className="cell-text"><span className="sub">southafricanorth</span></span>
                  </td>
                  <td className="col-tier">
                    <span className="cell-text"><span className="sub">southafricanorth</span></span>
                  </td>
                </tr>

              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ================================================================ TRUST SIGNALS */}
      <section className="trust">
        <div className="shell">
          <div className="section-head">
            <span className="eyebrow">Compliance posture</span>
            <h2>Three commitments <em>in writing.</em></h2>
          </div>

          <div className="trust-grid">
            <article className="trust-card">
              <div className="trust-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M12 2.5l8 3.5v6c0 4.5-3.5 8.4-8 9.5-4.5-1.1-8-5-8-9.5v-6l8-3.5z" />
                  <path d="M9 12l2 2 4-4.5" />
                </svg>
              </div>
              <h3 className="trust-title">POPIA-aligned by design.</h3>
              <p className="trust-body">
                Hosted in <code>southafricanorth</code>. Microsoft Azure South Africa North region
                (Cape Town and Johannesburg). Your data never leaves the country.
              </p>
            </article>

            <article className="trust-card">
              <div className="trust-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <rect x="4" y="3" width="16" height="18" rx="2" />
                  <path d="M8 8h8M8 12h8M8 16h5" />
                  <circle cx="17" cy="17" r="2.4" fill="currentColor" stroke="none" opacity="0.18" />
                </svg>
              </div>
              <h3 className="trust-title">FICA s23 immutable retention.</h3>
              <p className="trust-body">
                5-year WORM-locked audit storage. Signed exports, checkable with our key on
                request. Every signature, every change, every lodgement: recoverable on demand.
              </p>
            </article>

            <article className="trust-card">
              <div className="trust-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M3 7l9-4 9 4-9 4-9-4z" />
                  <path d="M3 12l9 4 9-4" />
                  <path d="M3 17l9 4 9-4" />
                </svg>
              </div>
              <h3 className="trust-title">No subprocessor surprises.</h3>
              <p className="trust-body">
                Subprocessors listed at <code>/legal/subprocessors</code>, updated in writing{' '}
                <b>30 days</b> before any change. No silent additions. Ever.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* ================================================================ FAQ */}
      <section className="faq">
        <div className="shell">
          <div className="section-head">
            <span className="eyebrow">Questions, answered plainly</span>
            <h2>Things firms ask <em>before they sign.</em></h2>
          </div>

          <div className="faq-list">

            <details className="faq-item" open>
              <summary className="faq-summary">
                <span className="faq-num mono">01</span>
                <span className="faq-q">What happens when my firm crosses 100 or 500 clients?</span>
                <span className="faq-toggle">
                  <PlusIcon />
                </span>
              </summary>
              <div className="faq-answer">
                <p>
                  You move automatically to the next tier on the first day of the next billing
                  cycle. <b>Practice (R 450) becomes Practice+ (R 395) at 100 clients</b>, and{' '}
                  <b>Practice+ becomes Enterprise (R 295) at 500</b>. The per-client rate drops as
                  you grow; there is no discontinuity in the bill and no migration ceremony:
                  same tenant, same audit chain, same URL.
                </p>
              </div>
            </details>

            <details className="faq-item">
              <summary className="faq-summary">
                <span className="faq-num mono">02</span>
                <span className="faq-q">How is the bill calculated each month?</span>
                <span className="faq-toggle">
                  <PlusIcon />
                </span>
              </summary>
              <div className="faq-answer">
                <p>
                  Two lines. <b>Base</b>: your tier rate multiplied by your billed client
                  count (or just the flat figure on Independent). <b>Overage</b>: the
                  per-tier per-trust rate, applied to every trust beyond the three included per
                  client. Both lines are itemised on the same invoice. Nothing else.
                </p>
              </div>
            </details>

            <details className="faq-item">
              <summary className="faq-summary">
                <span className="faq-num mono">03</span>
                <span className="faq-q">Do you charge per signature or per audit event?</span>
                <span className="faq-toggle">
                  <PlusIcon />
                </span>
              </summary>
              <div className="faq-answer">
                <p>
                  No. Resolutions, signatures, and audit events are unlimited at every tier.
                  The base price covers the client &middot; the overage covers extra trusts.
                  Audit events are part of the audit chain: the chain is the product, you
                  don&rsquo;t pay it by the line. Two lines on the invoice. Nothing else.
                </p>
              </div>
            </details>

            <details className="faq-item">
              <summary className="faq-summary">
                <span className="faq-num mono">04</span>
                <span className="faq-q">
                  What if I need a POPIA Data Processing Agreement or a Master Service Agreement?
                </span>
                <span className="faq-toggle">
                  <PlusIcon />
                </span>
              </summary>
              <div className="faq-answer">
                <p>
                  The POPIA Data Processor Agreement is included from Practice onward and signs at
                  activation. The Master Service Agreement is on standard terms for Practice and is
                  negotiable for Enterprise. Both are pre-cleared with our legal counsel; turnaround
                  on a marked-up redline is typically two business days.
                </p>
              </div>
            </details>

            <details className="faq-item">
              <summary className="faq-summary">
                <span className="faq-num mono">05</span>
                <span className="faq-q">How do we get started, and is there a free trial?</span>
                <span className="faq-toggle">
                  <PlusIcon />
                </span>
              </summary>
              <div className="faq-answer">
                <p>
                  We don&rsquo;t run a self-serve free trial: ingesting a firm&rsquo;s book
                  carries real processing cost, so we onboard every firm with a person. Book a
                  guided onboarding and we&rsquo;ll set up your tenant, bring in a slice of your
                  real book, and walk you through the audit chain, FICA lodgement and resolutions
                  on your own data before you commit to a plan.
                </p>
              </div>
            </details>

          </div>
        </div>
      </section>

      {/* ================================================================ CLOSER */}
      <section className="closer">
        <div className="shell">
          <div className="closer-card">
            <div>
              <span className="eyebrow on-dark">Talk to us</span>
              <h3>Bring your trust register. <em>We open a tenant on the call.</em></h3>
              <p>
                A 30-minute call with someone who has actually administered a trust. No SDR funnel.
                No demo theatre. We open a tenant on the call and you put a real resolution through it.
              </p>
            </div>
            <div className="closer-actions">
              <Link className="btn btn-brass" to="/auth/signup">Get started</Link>
              <Link className="btn btn-ghost" to="/contact">Book a 30-minute call</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================ FOOTER */}
      <footer className="site">
        <div className="shell">
          <div className="footer-grid">
            <div className="footer-brand">
              <div className="brand-row">
                <ReignMark size={34} />
                <span className="wordmark">elreign</span>
              </div>
              <p className="footer-tag">Quiet rigour for fiduciary work.</p>
            </div>

            <div className="footer-col">
              <h4>Product</h4>
              <ul>
                <li><a href="/product">Overview</a></li>
                <li><Link to="/pricing">Pricing</Link></li>
                <li><a href="/security">Security</a></li>
                <li><a href="/changelog">Changelog</a></li>
              </ul>
            </div>

            <div className="footer-col">
              <h4>Compliance</h4>
              <ul>
                <li><a href="/compliance/fica">FICA</a></li>
                <li><a href="/compliance/popia">POPIA</a></li>
                <li><a href="/legal/dpa">Data Processor Agreement</a></li>
                <li><Link to="/legal/subprocessors">Subprocessors</Link></li>
              </ul>
            </div>

            <div className="footer-col">
              <h4>Company</h4>
              <ul>
                <li><Link to="/about">About</Link></li>
                <li><a href="/customers">Customers</a></li>
                <li><Link to="/contact">Contact</Link></li>
                <li><a href="/careers">Careers</a></li>
              </ul>
            </div>

            <div className="footer-col">
              <h4>Resources</h4>
              <ul>
                <li><a href="/docs">Documentation</a></li>
                <li><a href="/brandbook">Brandbook</a></li>
                <li><a href="/status">Status</a></li>
                <li><Link to="/auth/login">Sign in</Link></li>
              </ul>
            </div>
          </div>

          <div className="footer-foot">
            <span>
              &copy; Elreign Legacy (Pty) Ltd &middot; Cape Town &middot;{' '}
              <span className="region">Hosted in Microsoft Azure South Africa North</span>
            </span>
            <div className="legal-links">
              <Link to="/legal/terms">Terms</Link>
              <Link to="/legal/privacy">Privacy</Link>
              <Link to="/legal/cookies">Cookies</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
